import logger from "../utils/LoggerFrontend";
import { dbPostsToPreviewData } from "../PagesUser/SelfServiceCampaignCreation/Previews/componentUtils";
import { getLeadFormLink } from "../utils/Helpers";
import { channelsByShortName } from "../data/app__channels";
import {
  getCreativeUrlForOtherChannel,
  getImageSpecs,
  isVideo
} from "../PagesUser/SelfServiceCampaignCreation/Previews/utils";
import database from "../utils/FirebaseController";

/**
 * Formats new posts by grouping them based on their channel.
 *
 * @param {Array} newPosts - Array of new posts to be formatted.
 * @returns {Object} An object containing formatted posts and channelsData.
 * @property {Object} posts - The posts grouped by channel.
 * @property {Object} channelsData - The channel data grouped by channel.
 */
export function formatNewPosts(newPosts) {
  return newPosts.reduce(
    (acc, { channel, channelData, ...postData }) => {
      const postDataWithChannel = { ...postData, channel };

      return {
        ...acc,
        posts: {
          ...acc.posts,
          [channel]: [...(acc.posts[channel] || []), postDataWithChannel]
        },
        channelsData: {
          ...acc.channelsData,
          [channel]: [...(acc.channelsData[channel] || []), channelData]
        }
      };
    },
    {
      posts: {},
      channelsData: {}
    }
  );
}

/**
 * Get an array of pictures from the different sources
 *
 * @param {{shutterstock: any[], logos: any[], images: any[]}} historicPictures - Picture object
 * @param {number} maxPictures - Maximum number of pictures to return
 */
export function getPictureArray(historicPictures, maxPictures = 50) {
  if (!historicPictures) {
    return [];
  }

  const { logos = [], images = [], shutterstock = [] } = historicPictures;
  return shutterstock.concat(images).concat(logos).slice(0, maxPictures);
}

/**
 * Get the default creatives for the ad preview
 *
 * @returns {Object[]} The default creatives
 */
export function getDefaultCreatives() {
  return [{ image_url: "/images/bild-vorschau.png" }]; // default fallback image
}

export async function formatPostPreviewData({ campaignData, posts, companyId }) {
  const jobPageLink = getLeadFormLink({
    jobCategoryId: campaignData.jobCategoryId || "",
    campaignId: campaignData.id,
    subdomain: campaignData.subdomain || "app",
    baseUrlTalentBait: window?.config?.baseUrlTalentBait
  });

  const postsPreviewData = await dbPostsToPreviewData({
    posts,
    channelsToUse: campaignData.selectedChannels.map(
      ({ channel_short_name }) => channel_short_name
    ),
    companyId
  });

  const newCommonPostData = {
    editedStates: {
      teaserText: false,
      jobPostingMarkup: false,
      logo: false,
      creatives: false
    },
    postNumber: 1,
    createdDate: new Date().toISOString(),
    updatedDate: null,
    lastUpdatedBy: null
  };

  const uneditedChannelStates = {
    pageId: false,
    pageLogo: false,
    pageName: false,
    subHeadline: false,
    displayLink: false,
    title: false,
    companyName: false,
    url: false
  };

  const createAndSavePostForNewChannel = async ({
    postsPreviewData,
    channelToCopy,
    channel,
    isReleased
  }) => {
    const limits = channelsByShortName[channel].limits;
    logger.log("PreviewStore:createAndSavePostForNewChannel", "called", {
      postsPreviewData,
      channelToCopy,
      channel,
      isReleased
    });

    // Channel data
    const channelDataToCopy =
      isReleased && postsPreviewData.released
        ? postsPreviewData.released.channelsData[channelToCopy]
        : postsPreviewData.channelsData[channelToCopy];

    // New Channel data
    const newChannelData = {
      ...channelDataToCopy,
      pageName: channelDataToCopy.pageName.substring(0, limits.pageName),
      subHeadline: channelDataToCopy.subHeadline.substring(0, limits.subHeadline),
      displayLink: channelDataToCopy.displayLink.substring(0, limits.displayLink),
      title: channelDataToCopy.title.substring(0, limits.title),
      companyName: channelDataToCopy.companyName.substring(0, limits.companyName),
      editedStates: uneditedChannelStates
    };

    // Post data
    const postDataToCopy = isReleased
      ? postsPreviewData.released.posts[channelToCopy][0]
      : postsPreviewData.posts[channelToCopy][0];
    // Set the default creative
    let creative = postDataToCopy.creatives[0];
    // Check if image is hosted on Cloudinary
    if (creative && creative.startsWith("https://res.cloudinary.com/")) {
      const imageSpecs = await getImageSpecs(creative);
      //Get the right creative for a given channel
      creative =
        imageSpecs &&
        getCreativeUrlForOtherChannel({
          channel,
          imageSpecs,
          imageUrl: creative
        })?.creatives?.[0];
    }

    const newPost = {
      ...postDataToCopy,
      ...newCommonPostData,
      creatives:
        creative && (!isVideo(creative) || channelsByShortName[channel].allowVideos)
          ? [creative]
          : getDefaultCreatives(),
      teaserText: postDataToCopy.teaserText.substring(0, limits.teaserText)
    };

    // Save post
    const resp = await database.saveAdCreatives([
      {
        ...newPost,
        channelData: newChannelData,
        type: isReleased ? "released" : "staging",
        stagingVersion: isReleased ? 0 : postsPreviewData.stagingVersion,
        releasedVersion: postsPreviewData.releasedVersion,
        channel
      }
    ]);
    newPost.id = resp[0].id;

    return { newPost, newChannelData };
  };

  await Promise.all(
    campaignData.channels.map(async (channel) => {
      // Create new post for each channel without a post
      if (!(channel in postsPreviewData.posts)) {
        logger.log(
          "PreviewStore:onGetCampaignAndAdCreativesByCampaignIdCompleted",
          "creating new post for channel",
          {
            channel,
            postsPreviewData
          }
        );

        const newChannelCategory = channelsByShortName[channel].category;
        const createdChannelWithSameCategory = Object.keys(postsPreviewData.posts).find(
          (createdChannel) => channelsByShortName[createdChannel].category === newChannelCategory
        );
        const channelToCopy =
          createdChannelWithSameCategory || Object.keys(postsPreviewData.posts)[0];

        const { newPost: newPostStaging, newChannelData: newChannelDataStaging } =
          await createAndSavePostForNewChannel({
            postsPreviewData,
            channelToCopy,
            channel,
            isReleased: false
          });

        const { newPost: newPostReleased, newChannelData: newChannelDataReleased } =
          await createAndSavePostForNewChannel({
            postsPreviewData,
            channelToCopy,
            channel,
            isReleased: true
          });

        postsPreviewData.posts[channel] = [newPostStaging];
        postsPreviewData.channelsData[channel] = newChannelDataStaging;
        postsPreviewData.released.posts[channel] = [newPostReleased];
        postsPreviewData.released.channelsData[channel] = newChannelDataReleased;
      }
    })
  );

  return { jobPageLink, postsPreviewData };
}
